import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { Formik, Form } from "formik"
import * as yup from "yup"
import { Modal, ModalBody } from "reactstrap"
import { Bounce, toast } from "react-toastify"
import Select from "react-select"
import { useSelector } from "react-redux"
import PageHeader from "../../../components/DashboardComponents/PageHeader"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import PasswordRequirementsHint from "../../../components/OnboardingComponents/PasswordRequirementsHint"
import EmptyState from "../../../components/Common/EmptyState"
import {
  ChevronDropdown,
  customStyles,
} from "../../../components/OnboardingComponents/RoleSelect"
import { ICONS } from "../../../constants/icons"
import defaultAvatar from "../../../assets/images/admin/avatar.png"
import ProfilePhoto from "../../Profile/ProfilePhoto"
import { get, patch, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { getApiFieldErrors } from "../../../helpers/formFieldErrors"
import useAdminPermission from "../../../hooks/useAdminPermission"
import {
  getCityAdminCitySelectOptions,
  isCityAdminUser,
} from "../../../helpers/cityPortalRoles"
import {
  PASSWORD_COMPLEXITY,
  PASSWORD_REQUIREMENTS,
  getPasswordFieldError,
  getPasswordHelperTone,
} from "../../../helpers/passwordRules"
import {
  buildStaffFormData,
  mapCitySelectOptions,
  mapRoleSelectOptions,
  mapStaffRecentActivities,
  mapStaffRow,
  STAFF_DETAIL_WITH,
} from "./adminStaffData"
import StaffCityMultiSelect from "./StaffCityMultiSelect"

const MAX_PHOTO_BYTES = 20 * 1024 * 1024

const ACTIVITY_ICONS = {
  user: ICONS.activityProfile,
  search: ICONS.adminMessageSearch,
  security: ICONS.activitySecurity,
  camera: ICONS.activityCamera,
  map: ICONS.activityMap,
}

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const passwordValidationSchema = yup.object().shape({
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(PASSWORD_COMPLEXITY, PASSWORD_REQUIREMENTS),
  passwordConfirmation: yup
    .string()
    .required("Please confirm the password")
    .oneOf([yup.ref("password")], "Passwords must match"),
})

const StaffPasswordModal = ({ isOpen, onClose, onSubmit, saving }) => (
  <Modal
    isOpen={isOpen}
    toggle={onClose}
    centered
    className="amok-modal amok-modal--password"
    contentClassName="amok-modal__content"
    backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
  >
    <ModalBody className="amok-modal__body edit-profile__modal-body">
      <Formik
        key={isOpen ? "staff-password-open" : "staff-password-closed"}
        initialValues={{ password: "", passwordConfirmation: "" }}
        validationSchema={passwordValidationSchema}
        validateOnChange
        validateOnBlur
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldTouched,
          isSubmitting,
        }) => (
          <Form className="edit-profile__modal-form">
            <div className="edit-profile__modal-main">
              <h3 className="edit-profile__modal-title">Change Password</h3>
              <div className="edit-profile__modal-fields">
                <div className="edit-profile__password-field">
                  <CustomInput
                    label="New Password"
                    type="password"
                    name="password"
                    placeholder="**********"
                    value={values.password}
                    onChange={event => {
                      handleChange(event)
                      setFieldTouched("password", true, false)
                    }}
                    onBlur={() => setFieldTouched("password", true)}
                    error={getPasswordFieldError(
                      touched.password,
                      errors.password,
                    )}
                    helperTone={getPasswordHelperTone(values.password)}
                  />
                  <PasswordRequirementsHint value={values.password} />
                </div>
                <CustomInput
                  label="Confirm Password"
                  type="password"
                  name="passwordConfirmation"
                  placeholder="**********"
                  value={values.passwordConfirmation}
                  onChange={handleChange}
                  onBlur={() => setFieldTouched("passwordConfirmation", true)}
                  error={
                    touched.passwordConfirmation && errors.passwordConfirmation
                  }
                />
              </div>
            </div>
            <div className="edit-profile__modal-actions">
              <CustomButton
                variant="danger"
                size="md"
                type="button"
                onClick={onClose}
                disabled={isSubmitting || saving}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="primary"
                size="md"
                type="submit"
                loading={isSubmitting || saving}
                disabled={isSubmitting || saving}
              >
                Update Password
              </CustomButton>
            </div>
          </Form>
        )}
      </Formik>
    </ModalBody>
  </Modal>
)

StaffPasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  saving: PropTypes.bool,
}

StaffPasswordModal.defaultProps = {
  saving: false,
}

/**
 * Admin Staff Profile — GET/POST /api/v1/admin/staff/*
 */
const StaffProfile = () => {
  const navigate = useNavigate()
  const { staffId } = useParams()
  const currentUser = useSelector(state => state.auth.currentUser)
  const isCityAdmin = isCityAdminUser(currentUser)
  const cityAdminOptions = useMemo(
    () => getCityAdminCitySelectOptions(currentUser),
    [currentUser],
  )
  const { can } = useAdminPermission()
  const canEdit = can("staff", "edit")
  const canViewActivityLogs = can("activity-logs", "view")
  const fileInputRef = useRef(null)
  const [photoPreview, setPhotoPreview] = useState(null)
  const [profileImage, setProfileImage] = useState(null)
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [statusSaving, setStatusSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [staffMember, setStaffMember] = useState(null)
  const [roleOptions, setRoleOptions] = useState([])
  const [cityOptions, setCityOptions] = useState([])
  const [recentActivities, setRecentActivities] = useState([])
  const [isActivityLoading, setIsActivityLoading] = useState(true)

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    roleId: "",
    cityIds: [],
    email: "",
    notes: "",
  })
  const [fieldErrors, setFieldErrors] = useState({})

  const applyStaffToForm = useCallback(
    mapped => {
      const allowed = new Set(cityAdminOptions.map(option => String(option.value)))
      const nextCityIds = (mapped.cityIds || []).map(String)
      setStaffMember(mapped)
      setForm({
        firstName: mapped.firstName || "",
        lastName: mapped.lastName || "",
        roleId: mapped.roleId || mapped.roleIds?.[0] || "",
        cityIds: isCityAdmin
          ? nextCityIds.filter(id => allowed.has(id))
          : nextCityIds,
        email: mapped.email === "--" ? "" : mapped.email || "",
        notes: mapped.notes || "",
      })
      setProfileImage(null)
      setPhotoPreview(prev => {
        if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev)
        return mapped.image || null
      })
    },
    [cityAdminOptions, isCityAdmin],
  )

  const loadStaff = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const rolesPromise = get(endpoints.getAdminRoles, {
        params: { page: 1, page_size: 100 },
      })
      const citiesPromise = isCityAdmin
        ? Promise.resolve(null)
        : get(endpoints.getAdminCities, {
            params: { page: 1, page_size: 100 },
          })
      const [detailResponse, rolesResponse, citiesResponse] = await Promise.all(
        [
          get(endpoints.getAdminStaffById(staffId), {
            params: { "with[]": STAFF_DETAIL_WITH },
          }),
          rolesPromise,
          citiesPromise,
        ],
      )

      if (rolesResponse?.data?.success) {
        setRoleOptions(
          mapRoleSelectOptions(rolesResponse.data.result?.data || []),
        )
      }

      if (isCityAdmin) {
        setCityOptions(cityAdminOptions)
      } else if (citiesResponse?.data?.success) {
        setCityOptions(
          mapCitySelectOptions(citiesResponse.data.result?.data || []),
        )
      }

      if (detailResponse?.data?.success) {
        applyStaffToForm(mapStaffRow(detailResponse.data.result || {}))
      } else {
        setLoadError(
          toastApiError(detailResponse, "Failed to load staff profile"),
        )
        setStaffMember(null)
      }
    } catch (err) {
      setLoadError(toastApiError(err, "Failed to load staff profile"))
      setStaffMember(null)
    } finally {
      setIsLoading(false)
    }
  }, [applyStaffToForm, cityAdminOptions, isCityAdmin, staffId])

  useEffect(() => {
    document.title = "Staff Profile | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    loadStaff()
  }, [loadStaff])

  useEffect(() => {
    if (!staffId || !canViewActivityLogs) {
      setRecentActivities([])
      setIsActivityLoading(false)
      return undefined
    }

    let cancelled = false
    const fetchRecentActivities = async () => {
      setIsActivityLoading(true)
      try {
        const response = await get(endpoints.getAdminActivityLogs, {
          params: {
            subject_type: "reward",
            user_id: staffId,
            sort_by: "created_at",
            sort_direction: "desc",
            page_size: 5,
          },
        })
        if (cancelled) return
        if (response?.data?.success) {
          setRecentActivities(
            mapStaffRecentActivities(response.data.result || {}, 5),
          )
        } else {
          toastApiError(response, "Failed to load recent activities")
          setRecentActivities([])
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load recent activities")
          setRecentActivities([])
        }
      } finally {
        if (!cancelled) setIsActivityLoading(false)
      }
    }

    fetchRecentActivities()
    return () => {
      cancelled = true
    }
  }, [staffId, canViewActivityLogs])

  useEffect(
    () => () => {
      if (photoPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(photoPreview)
      }
    },
    [photoPreview],
  )

  const handleFieldChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: "" }))
    }
  }

  const handlePhotoChange = event => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Photo must be 20MB or smaller.", { transition: Bounce })
      event.target.value = ""
      return
    }

    if (photoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(photoPreview)
    }

    setProfileImage(file)
    setPhotoPreview(URL.createObjectURL(file))
    event.target.value = ""
  }

  const handleToggleStatus = async () => {
    if (!staffMember?.id || statusSaving) return
    const nextActive = !staffMember.isActive
    setStatusSaving(true)
    try {
      const response = await patch(
        endpoints.updateAdminStaffStatus(staffMember.id),
        { is_active: nextActive },
        { skipErrorToast: true },
      )
      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        toastApiError(response, "Failed to update staff status")
        return
      }
      toast.success(
        response?.data?.message ||
          (nextActive
            ? "Staff member activated."
            : "Staff member deactivated."),
        TOAST_OPTIONS,
      )
      await loadStaff()
    } catch (err) {
      toastApiError(err, "Failed to update staff status")
    } finally {
      setStatusSaving(false)
    }
  }

  const handlePasswordSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const formData = buildStaffFormData({
        firstName: form.firstName,
        lastName: form.lastName,
        roleId: form.roleId,
        cityIds: form.cityIds,
        notes: form.notes,
        password: values.password,
        passwordConfirmation: values.passwordConfirmation,
        includeNotes: true,
        includePassword: true,
      })

      const response = await post(
        endpoints.updateAdminStaff(staffId),
        formData,
        { skipErrorToast: true },
      )
      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        toastApiError(response, "Failed to update password")
        return
      }
      toast.success(
        response?.data?.message || "Password updated successfully.",
        TOAST_OPTIONS,
      )
      resetForm()
      setPasswordModalOpen(false)
    } catch (err) {
      toastApiError(err, "Failed to update password")
    } finally {
      setSubmitting(false)
    }
  }

  const handleSave = async () => {
    const nextErrors = {}
    if (!form.firstName.trim()) {
      nextErrors.firstName = "First name is required."
    }
    if (!form.lastName.trim()) {
      nextErrors.lastName = "Last name is required."
    }
    if (!form.roleId) {
      nextErrors.roleId = "Select a role."
    }
    if (!form.cityIds.length) {
      nextErrors.cityIds = "Select at least one city."
    }
    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors)
      return
    }
    setFieldErrors({})

    setSaving(true)
    try {
      const formData = buildStaffFormData({
        firstName: form.firstName,
        lastName: form.lastName,
        roleId: form.roleId,
        cityIds: form.cityIds,
        notes: form.notes,
        profileImage,
        includeNotes: true,
      })

      const response = await post(
        endpoints.updateAdminStaff(staffId),
        formData,
        { skipErrorToast: true },
      )
      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        const apiFieldErrors = getApiFieldErrors(response, {
          first_name: "firstName",
          last_name: "lastName",
          role_id: "roleId",
          city_ids: "cityIds",
        })
        if (Object.keys(apiFieldErrors).length) {
          setFieldErrors(apiFieldErrors)
          return
        }
        toastApiError(response, "Failed to update staff profile")
        return
      }
      toast.success(
        response?.data?.message || "Staff profile updated successfully.",
        TOAST_OPTIONS,
      )
      navigate("/admin/staff")
    } catch (err) {
      toastApiError(err, "Failed to update staff profile")
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div
        className="admin-page admin-staff-profile"
        aria-busy="true"
        aria-label="Loading staff profile"
      >
        <div className="admin-staff-profile__header admin-staff-profile__header--skeleton">
          <div className="admin-staff-profile__skeleton admin-staff-profile__skeleton--title" />
          <div className="admin-staff-profile__skeleton admin-staff-profile__skeleton--subtitle" />
        </div>
        <div className="admin-staff-profile__fields">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`staff-skel-${index}`}
              className="admin-staff-profile__row admin-staff-profile__row--two"
            >
              <div className="admin-staff-profile__skeleton admin-staff-profile__skeleton--field" />
              <div className="admin-staff-profile__skeleton admin-staff-profile__skeleton--field" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!staffMember && loadError) {
    return (
      <div className="admin-page admin-staff-profile">
        <EmptyState
          title="Unable to load staff profile"
          description={loadError}
          actionText="Try again"
          onActionClick={loadStaff}
        />
        <CustomButton
          variant="secondary"
          size="md"
          type="button"
          onClick={() => navigate("/admin/staff")}
        >
          Back to Staff
        </CustomButton>
      </div>
    )
  }

  if (!staffMember) {
    return <Navigate to="/admin/staff" replace />
  }

  const selectedRole =
    roleOptions.find(option => option.value === form.roleId) || null

  return (
    <div className="admin-page admin-staff-profile">
      <PageHeader
        className="admin-staff-profile__header"
        title="Staff Profile"
        subtitle="Update staff profile."
        actions={
          canEdit ? (
            <>
              <CustomButton
                variant="secondary"
                size="md"
                type="button"
                onClick={() => setPasswordModalOpen(true)}
                disabled={saving || statusSaving}
              >
                Change Password
              </CustomButton>
              <CustomButton
                variant="danger"
                size="md"
                type="button"
                onClick={handleToggleStatus}
                loading={statusSaving}
                disabled={saving || statusSaving}
              >
                {staffMember.isActive ? "Deactivate" : "Activate"}
              </CustomButton>
              <CustomButton
                variant="primary"
                size="md"
                type="button"
                onClick={handleSave}
                loading={saving}
                disabled={saving || statusSaving}
                trailingIconSrc={ICONS.saveAdd}
                trailingIconAlt=""
              >
                Update Profile
              </CustomButton>
            </>
          ) : null
        }
      />

      <section className="admin-staff-profile__photo-section">
        <div className="admin-staff-profile__photo-row">
          <ProfilePhoto
            src={photoPreview || defaultAvatar}
            name={staffMember.name}
            className="admin-staff-profile__photo"
          />
          <div className="admin-staff-profile__photo-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              className="admin-staff-profile__file-input"
              onChange={handlePhotoChange}
            />
            {canEdit ? (
              <CustomButton
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => fileInputRef.current?.click()}
              >
                Change Photo
              </CustomButton>
            ) : null}
            <p className="admin-staff-profile__photo-hint">
              JPG or PNG. Max size of 20MB
            </p>
          </div>
        </div>
      </section>

      <div className="admin-staff-profile__fields">
        <div className="admin-staff-profile__row admin-staff-profile__row--three">
          <CustomInput
            label="First Name"
            name="firstName"
            placeholder="Enter first name"
            value={form.firstName}
            onChange={event =>
              handleFieldChange("firstName", event.target.value)
            }
            error={fieldErrors.firstName}
          />
          <CustomInput
            label="Last Name"
            name="lastName"
            placeholder="Enter last name"
            value={form.lastName}
            onChange={event =>
              handleFieldChange("lastName", event.target.value)
            }
            error={fieldErrors.lastName}
          />
          <div className={`amok-field ${fieldErrors.roleId ? "amok-field--error" : ""}`.trim()}>
            <label className="amok-field__label" htmlFor="staff-profile-role">
              Role
            </label>
            <Select
              inputId="staff-profile-role"
              className={`amok-role-select ${
                fieldErrors.roleId ? "amok-role-select--error" : ""
              }`}
              classNamePrefix="amok-role-select"
              options={roleOptions}
              value={selectedRole}
              onChange={option =>
                handleFieldChange("roleId", option?.value || "")
              }
              placeholder="Select role"
              styles={customStyles(Boolean(fieldErrors.roleId))}
              isSearchable={false}
              isClearable={false}
              components={{
                DropdownIndicator: ChevronDropdown,
                IndicatorSeparator: null,
              }}
            />
            {fieldErrors.roleId ? (
              <p className="amok-field__message amok-field__message--error">
                {fieldErrors.roleId}
              </p>
            ) : null}
          </div>
        </div>

        <CustomInput
          label="Email Address"
          name="email"
          type="email"
          placeholder="Enter email address"
          value={form.email}
          disabled
          onChange={event => handleFieldChange("email", event.target.value)}
        />

        <StaffCityMultiSelect
          id="staff-profile-cities"
          label="City"
          options={cityOptions}
          value={form.cityIds}
          onChange={next => handleFieldChange("cityIds", next)}
          error={fieldErrors.cityIds}
        />

        <CustomInput
          label="Notes"
          name="notes"
          textarea
          rows={4}
          placeholder="Add internal notes about this staff member..."
          value={form.notes}
          onChange={event => handleFieldChange("notes", event.target.value)}
        />
      </div>

      {canViewActivityLogs ? (
        <section
          className="admin-staff-profile__activities"
          aria-busy={isActivityLoading || undefined}
        >
          <h2 className="admin-staff-profile__activities-title">
            Recent Activities
          </h2>
          <ul className="admin-staff-profile__activities-list">
            {isActivityLoading &&
              Array.from({ length: 4 }).map((_, index) => (
                <li
                  key={`activity-skel-${index}`}
                  className="admin-staff-profile__activity-row admin-staff-profile__activity-row--skeleton"
                  aria-hidden="true"
                >
                  <span className="admin-staff-profile__skeleton admin-staff-profile__skeleton--avatar" />
                  <div className="admin-staff-profile__activity-copy">
                    <span className="admin-staff-profile__skeleton admin-staff-profile__skeleton--line" />
                    <span className="admin-staff-profile__skeleton admin-staff-profile__skeleton--line-sm" />
                  </div>
                  <span className="admin-staff-profile__skeleton admin-staff-profile__skeleton--time" />
                </li>
              ))}

            {!isActivityLoading &&
              recentActivities.map(item => (
                <li key={item.id} className="admin-staff-profile__activity-row">
                  <span
                    className="admin-staff-profile__activity-icon"
                    aria-hidden="true"
                  >
                    <img
                      src={ACTIVITY_ICONS[item.iconKey] || ACTIVITY_ICONS.user}
                      alt=""
                    />
                  </span>
                  <div className="admin-staff-profile__activity-copy">
                    <p className="admin-staff-profile__activity-title">
                      {item.title}
                    </p>
                    <p className="admin-staff-profile__activity-subtitle">
                      {item.subtitle}
                    </p>
                  </div>
                  <span className="admin-staff-profile__activity-time">
                    {item.time}
                  </span>
                </li>
              ))}

            {!isActivityLoading && recentActivities.length === 0 && (
              <li className="admin-staff-profile__activities-empty">
                <EmptyState
                  title="No recent activity"
                  description="Staff activity will show up here once available."
                />
              </li>
            )}
          </ul>
        </section>
      ) : null}

      <StaffPasswordModal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        onSubmit={handlePasswordSubmit}
        saving={saving}
      />
    </div>
  )
}

export default StaffProfile
